// CategoryId.jsx
import React from 'react';
import { useLoaderData } from 'react-router';

import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { getSupportedLanguages } from '../selector/getRemoteConfigComputedData.js';
import useSSRDataMerge, { resetSSRDataMerge } from '../hook/useSSRDataMerge.js';
import { CATEGORY_FEED_ALL } from '../RemoteConfigKeys.js';
import { prepareCollectionPageMeta } from '../resource/prepareCollectionPageMeta.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { fetchFeedTotalCount } from '../resource/fetchFeedTotalCount.js';
import { generateCollectionPageJsonLd } from '../resource/generateCollectionPageJsonLd.js';
import { createInitialStoreWithRequest } from '../createInitialStore.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('CategoryId loader()');
  const errorLog = legacySSRError.extend('CategoryId loader()');
  log('start', { context, params, request });

  // Reset to re-merge SSR data
  resetSSRDataMerge();

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    // Get category feeds from remote config
    let state = store.getState();
    const { categoryId } = params;
    const categoryFeeds = getRemoteConfigData(
      state,
      CATEGORY_FEED_ALL,
      undefined,
      {
        CATEGORY: categoryId,
      }
    );

    // Fetch all category feeds count
    let totalFeedCount = 0;
    if (categoryFeeds && categoryFeeds.length > 0) {
      const feedCountResults = await Promise.allSettled(
        categoryFeeds.map(categoryFeedName =>
          fetchFeedTotalCount({
            feedNameWithQuery: categoryFeedName,
            httpProxyHeaders,
          })
        )
      );

      // Sum up all feed counts
      totalFeedCount = feedCountResults.reduce((sum, result) => {
        return sum + (result.status === 'fulfilled' ? result.value : 0);
      }, 0);
    }

    state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData, totalFeedCount });

    // Use i18n instance from loader
    const { t } = i18nInstance;

    // Get category name from translation
    const categoryName = t(`category_${categoryId}`, {
      ns: TranslationNamespace.FEED,
    });

    // Prepare title and description
    const title = t('seo_category_all_title', {
      ns: TranslationNamespace.SEO,
      category: categoryName,
    });

    const description =
      t(`seo_meta_template_description_category_${categoryId}`, {
        ns: TranslationNamespace.SEO,
      }) ||
      t('seo_category_all_description', {
        ns: TranslationNamespace.SEO,
        category: categoryName,
      });

    const availableLanguages = getSupportedLanguages(state);
    const canonicalPath = `/category/${categoryId}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Generate JSON-LD
    const hostname = request?.headers?.get('host') || 'swag.live';
    const origin = `https://${hostname}`;

    const jsonLd = await generateCollectionPageJsonLd({
      origin,
      categoryId,
      state,
      i18n: i18nInstance,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareCollectionPageMeta({
      state,
      title,
      description,
      keywords: [categoryName, 'SWAG', 'category'],
      hostname,
      canonical,
      alternates,
      jsonLd,
    });

    log('meta data prepared', { meta });

    return {
      data: { store: {} }, // We don't need to pass store data for now
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname,
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const CategoryIdRoute = () => {
  const { data, error } = useLoaderData();

  useSSRDataMerge({
    componentName: 'CategoryId',
    loaderData: data,
    loaderError: error,
  });

  return (
    <UniversalComponent
      filename="Container_MessageCategoryTabAllInOne"
      importer={
        /* #__LOADABLE__ */ () =>
          import('../container/MessageCategoryTabAllInOne.js')
      }
    />
  );
};

export default CategoryIdRoute;
